// shell/components/table/table-renderer.ts

import { ColumnDef, RenderedCell } from './types';
import { ColumnManager } from './column-manager';
import { ColumnRendererFactory } from './column-renderer-factory';
import { CellRenderContext, CellUpdateType } from './columns/column-base';
import { HeatMapBounds } from '../heat-map';
import { RowState, IDisposable } from '../types';
import { IRenderer } from '../renderer-types';
import {
    AnimationTimeoutManager,
    EventListenerTracker,
    computeShadeRank,
    safeGetValue,
    findInsertionIndex,
    FRESH_HIGHLIGHT_DURATION_MS
} from '../renderer-utils';

/**
 * Rendered row tracking for updates.
 * Event handlers read from this object to avoid stale closures.
 */
interface RenderedRow<T> {
    /** The row element */
    element: HTMLElement;
    /** Row index (mutable - updated on recycle) */
    index: number;
    /** Item at render time (mutable - updated on recycle) */
    item: T;
    /** Rendered cells for each column */
    cells: Map<string, RenderedCell<T>>;
    /** Checkbox cell (if selectable) */
    checkboxCell?: HTMLElement;
}

/**
 * Configuration for TableRenderer.
 */
export interface TableRendererConfig<T> {
    /** Root element for CSS variable (must be ancestor of both header and body) */
    rootElement: HTMLElement;

    /** Column manager instance */
    columnManager: ColumnManager<T>;

    /** Column renderer factory (owns shared HeatMapShader) */
    rendererFactory: ColumnRendererFactory<T>;

    /** Extract row ID from item */
    getRowId: (item: T) => string;

    /** Get visual state for row */
    getRowState?: (item: T) => RowState | undefined;

    /** Custom CSS class for row */
    getRowClass?: (item: T, index: number) => string | undefined;

    /** Row height in pixels (default: 36) */
    rowHeight?: number;

    /** Total item count for ARIA (updated via setTotalCount) */
    totalCount?: number;

    /** Duration for fresh highlight animation in ms (default: 2000) */
    freshHighlightDuration?: number;

    // === Events ===

    /** Row clicked */
    onRowClick?: (item: T, index: number, event: MouseEvent) => void;

    /** Row double-clicked */
    onRowDoubleClick?: (item: T, index: number, event: MouseEvent) => void;

    /** Cell clicked */
    onCellClick?: (item: T, columnKey: string, event: MouseEvent) => void;

    /** Header cell clicked (for sorting) */
    onHeaderClick?: (columnKey: string, event: MouseEvent) => void;

    /** Keyboard event on focused row (return true if handled) */
    onKeyDown?: (event: KeyboardEvent, focusedItem: T | null) => boolean;

    // === Selection integration ===

    /** Whether selection is enabled */
    selectable?: boolean;

    /** Check if item is selected (checkbox state for bulk actions) */
    isSelected?: (item: T) => boolean;

    /** Check if item is the active/focused row (master-detail highlight) */
    isActive?: (item: T) => boolean;

    /** Toggle selection on item */
    onSelectionToggle?: (item: T, event: MouseEvent) => void;

    // === Sorting display ===

    /** Currently sorted column key */
    sortColumn?: string;

    /** Sort direction */
    sortDirection?: 'asc' | 'desc';

    // === Row preprocessing ===

    /** Called before row is rendered */
    onRowRender?: (element: HTMLElement, item: T, index: number) => void;

    /** Called after row is rendered */
    onRowRendered?: (element: HTMLElement, item: T, index: number) => void;
}

/**
 * TableRenderer creates table row elements for visible items.
 *
 * Key responsibilities:
 * - Build row DOM elements with cells for each visible column
 * - Delegate cell rendering to ColumnRendererFactory (with error boundaries)
 * - Handle selection checkbox column
 * - Manage column template via CSS custom property
 * - Apply row state CSS classes
 * - Emit events for row/cell interactions
 * - Keyboard navigation between rows
 *
 * Does NOT handle:
 * - Scroll position or virtualization (VirtualScroller's job)
 * - Data fetching (DataSource's job)
 * - Selection state tracking (SelectionManager's job)
 * - Heat map shader instance (ColumnRendererFactory's job)
 */
export class TableRenderer<T> implements IRenderer<T> {
    private readonly config: TableRendererConfig<T>;
    private readonly rootElement: HTMLElement;
    private readonly columnManager: ColumnManager<T>;
    private readonly rendererFactory: ColumnRendererFactory<T>;
    private readonly timeoutManager = new AnimationTimeoutManager();
    private readonly eventTracker = new EventListenerTracker();
    private readonly freshDuration: number;

    private container: HTMLElement | null = null;
    private headerElement: HTMLElement | null = null;
    private renderedRows = new Map<number, RenderedRow<T>>();
    private focusedIndex = -1;
    private rowHeight: number;
    private totalCount = 0;
    private selectable: boolean;

    // Cached column template string
    private cachedColumnTemplate: string | null = null;

    // Cached per render batch to avoid repeated calls
    private cachedColumns: ColumnDef<T>[] | null = null;
    private cachedHeatMapBounds: Map<string, HeatMapBounds> | null = null;

    constructor(config: TableRendererConfig<T>) {
        this.config = config;
        this.rootElement = config.rootElement;
        this.columnManager = config.columnManager;
        this.rendererFactory = config.rendererFactory;
        this.rowHeight = config.rowHeight ?? 36;
        this.totalCount = config.totalCount ?? 0;
        this.selectable = config.selectable ?? false;
        this.freshDuration = config.freshHighlightDuration ?? FRESH_HIGHLIGHT_DURATION_MS;

        // Set initial column template
        this.updateColumnTemplate();
    }

    /**
     * Update the CSS custom property for column template.
     * Called when columns change or selectable state changes.
     */
    private updateColumnTemplate(): void {
        const columns = this.columnManager.getVisibleColumns();
        const widths = columns.map(c => {
            // Check for table-specific width override
            const tableWidth = c.table?.width;
            if (tableWidth !== undefined) {
                if (tableWidth === 'auto') return 'auto';
                if (typeof tableWidth === 'number') return `${tableWidth}px`;
                return tableWidth;
            }
            // Fall back to general width
            if (typeof c.width === 'number') return `${c.width}px`;
            if (typeof c.width === 'string') return c.width;
            return 'minmax(100px, 1fr)'; // Default flexible
        });

        const template = this.selectable ? ['40px', ...widths] : widths;
        this.cachedColumnTemplate = template.join(' ');
        this.rootElement.style.setProperty('--cs-table-columns', this.cachedColumnTemplate);
    }

    render(container: HTMLElement, items: T[], startIndex: number): void {
        this.container = container;

        // Cache columns and heat map bounds for this render batch
        this.cachedColumns = this.columnManager.getVisibleColumns();
        this.cachedHeatMapBounds = this.getHeatMapBounds(this.cachedColumns);

        // Determine which rows need to be added/removed
        const newIndices = new Set<number>();
        for (let i = 0; i < items.length; i++) {
            if (items[i] != null) {
                newIndices.add(startIndex + i);
            }
        }
        const existingIndices = new Set(this.renderedRows.keys());

        // Remove rows no longer in range
        for (const index of existingIndices) {
            if (!newIndices.has(index)) {
                const row = this.renderedRows.get(index)!;
                this.cleanupRow(row);
                row.element.remove();
                this.renderedRows.delete(index);
                this.timeoutManager.cancel(`fresh-${index}`);
            }
        }

        // Collect new rows to add using DocumentFragment for batch insertion
        const newRows: Array<{ row: RenderedRow<T>; index: number }> = [];

        // Add/update rows
        for (let i = 0; i < items.length; i++) {
            const index = startIndex + i;
            const item = items[i];

            // Skip null/undefined items
            if (item == null) continue;

            if (this.renderedRows.has(index)) {
                // Update existing row (handles row recycling)
                this.updateRowElement(this.renderedRows.get(index)!, item, index);
            } else {
                // Create new row
                const row = this.createRowElement(item, index);
                newRows.push({ row, index });
            }
        }

        // Sort new rows by index for correct insertion order
        newRows.sort((a, b) => a.index - b.index);

        // Insert new rows in batches
        if (newRows.length > 0) {
            this.insertRowsBatched(newRows);
        }

        // Clear cache after render
        this.cachedColumns = null;
        this.cachedHeatMapBounds = null;
    }

    private insertRowsBatched(
        newRows: Array<{ row: RenderedRow<T>; index: number }>
    ): void {
        if (!this.container) return;

        // Get sorted list of existing indices
        const existingIndices = Array.from(this.renderedRows.keys()).sort((a, b) => a - b);

        let currentBatch: HTMLElement[] = [];
        let insertBeforeIndex: number | null = null;

        for (const { row, index } of newRows) {
            // Find where this row should be inserted
            const targetInsertBefore = this.findInsertBeforeIndex(existingIndices, index);

            if (insertBeforeIndex === null) {
                insertBeforeIndex = targetInsertBefore;
            }

            if (targetInsertBefore === insertBeforeIndex) {
                // Same insertion point, add to batch
                currentBatch.push(row.element);
            } else {
                // Different insertion point, flush current batch
                this.flushBatch(currentBatch, insertBeforeIndex);
                currentBatch = [row.element];
                insertBeforeIndex = targetInsertBefore;
            }

            this.renderedRows.set(index, row);
            existingIndices.splice(
                findInsertionIndex(existingIndices, index),
                0,
                index
            );
        }

        // Flush remaining batch
        if (currentBatch.length > 0) {
            this.flushBatch(currentBatch, insertBeforeIndex);
        }
    }

    private findInsertBeforeIndex(sortedIndices: number[], targetIndex: number): number | null {
        const pos = findInsertionIndex(sortedIndices, targetIndex);
        return pos < sortedIndices.length ? sortedIndices[pos] : null;
    }

    private flushBatch(elements: HTMLElement[], insertBeforeIndex: number | null): void {
        if (!this.container || elements.length === 0) return;

        const fragment = document.createDocumentFragment();
        for (const el of elements) {
            fragment.appendChild(el);
        }

        if (insertBeforeIndex !== null) {
            const beforeRow = this.renderedRows.get(insertBeforeIndex);
            if (beforeRow) {
                this.container.insertBefore(fragment, beforeRow.element);
                return;
            }
        }

        this.container.appendChild(fragment);
    }

    private createRowElement(item: T, index: number): RenderedRow<T> {
        const row = document.createElement('div');
        row.className = 'cs-table-row';
        row.setAttribute('role', 'row');
        row.setAttribute('data-index', String(index));
        row.setAttribute('data-id', this.config.getRowId(item));
        row.setAttribute('aria-rowindex', String(index + 1)); // 1-based for ARIA
        row.tabIndex = -1;

        // Create the RenderedRow object FIRST so event handlers can reference it
        const renderedRow: RenderedRow<T> = {
            element: row,
            index,
            item,
            cells: new Map(),
            checkboxCell: undefined
        };

        // Apply row state classes
        this.applyRowState(row, item);

        // Custom class
        const customClass = this.config.getRowClass?.(item, index);
        if (customClass) row.classList.add(customClass);

        // Pre-render hook
        this.config.onRowRender?.(row, item, index);

        // Selection checkbox (if enabled)
        if (this.selectable) {
            renderedRow.checkboxCell = this.addCheckboxCell(row, renderedRow);
        }

        // Render cells for each visible column via factory
        const columns = this.cachedColumns ?? this.columnManager.getVisibleColumns();
        const heatMapBoundsMap = this.cachedHeatMapBounds ?? this.getHeatMapBounds(columns);

        for (const column of columns) {
            // Skip table-ignored columns
            if (column.table?.ignore) continue;

            const cellWrapper = document.createElement('div');
            cellWrapper.className = 'cs-table-cell';
            cellWrapper.setAttribute('role', 'gridcell');
            cellWrapper.setAttribute('data-column', column.key);

            // Get value safely
            const value = safeGetValue(column, item);

            const bounds = heatMapBoundsMap.get(column.key);
            const shadeRank = computeShadeRank(value, bounds, column, item);

            const ctx: CellRenderContext<T> = {
                column,
                item,
                value,
                rowIndex: index,
                heatMapBounds: bounds,
                shadeRank
            };

            // Delegate to factory (handles error boundaries)
            const renderedCell = this.rendererFactory.renderTableCell(
                column,
                ctx,
                cellWrapper
            );

            renderedRow.cells.set(column.key, renderedCell);

            // Cell click handler - reads from renderedRow to get current item
            const cellClickHandler = (e: Event) => {
                this.config.onCellClick?.(renderedRow.item, column.key, e as MouseEvent);
            };
            cellWrapper.addEventListener('click', cellClickHandler);

            row.appendChild(cellWrapper);
        }

        // Event handlers - reference renderedRow object, not closures over item/index
        this.attachRowEvents(row, renderedRow);

        // Post-render hook
        this.config.onRowRendered?.(row, item, index);

        return renderedRow;
    }

    private updateRowElement(row: RenderedRow<T>, item: T, index: number): void {
        // Update mutable fields - event handlers read from these
        row.index = index;
        row.item = item;

        // Update data attributes
        row.element.setAttribute('data-index', String(index));
        row.element.setAttribute('data-id', this.config.getRowId(item));
        row.element.setAttribute('aria-rowindex', String(index + 1));

        // Update row state
        this.applyRowState(row.element, item);

        // Update checkbox if present
        if (row.checkboxCell && this.selectable) {
            this.updateCheckbox(row.checkboxCell, item);
        }

        // Update each cell via factory
        const columns = this.cachedColumns ?? this.columnManager.getVisibleColumns();
        const heatMapBoundsMap = this.cachedHeatMapBounds ?? this.getHeatMapBounds(columns);

        for (const column of columns) {
            if (column.table?.ignore) continue;

            const renderedCell = row.cells.get(column.key);
            if (!renderedCell) {
                // New column added - must call onColumnsChanged() to handle properly
                console.warn(`Column ${column.key} not found in rendered row. Call onColumnsChanged() when columns change.`);
                continue;
            }

            const value = safeGetValue(column, item);
            const bounds = heatMapBoundsMap.get(column.key);
            const shadeRank = computeShadeRank(value, bounds, column, item);

            const ctx: CellRenderContext<T> = {
                column,
                item,
                value,
                rowIndex: index,
                heatMapBounds: bounds,
                shadeRank
            };

            // Update via factory (handles error boundaries, returns new RenderedCell)
            const updatedCell = this.rendererFactory.updateTableCell(
                renderedCell,
                ctx,
                CellUpdateType.Full
            );

            row.cells.set(column.key, updatedCell);
        }
    }

    private cleanupRow(row: RenderedRow<T>): void {
        // Cleanup all cells via factory
        for (const cell of row.cells.values()) {
            this.rendererFactory.cleanupCell(cell);
        }
        row.cells.clear();
    }

    private addCheckboxCell(row: HTMLElement, renderedRow: RenderedRow<T>): HTMLElement {
        const cell = document.createElement('div');
        cell.className = 'cs-table-cell cs-selector';

        const checkbox = document.createElement('button');
        checkbox.type = 'button';
        checkbox.className = 'cs-checkbox';
        checkbox.setAttribute('aria-checked', String(this.config.isSelected?.(renderedRow.item) ?? false));
        checkbox.setAttribute('aria-label', 'Select row');

        if (this.config.isSelected?.(renderedRow.item)) {
            checkbox.classList.add('selected');
        }

        // Read from renderedRow to get current item
        const clickHandler = (e: Event) => {
            e.stopPropagation();
            this.config.onSelectionToggle?.(renderedRow.item, e as MouseEvent);
        };
        checkbox.addEventListener('click', clickHandler);

        cell.appendChild(checkbox);
        row.insertBefore(cell, row.firstChild);
        return cell;
    }

    private updateCheckbox(cell: HTMLElement, item: T): void {
        const checkbox = cell.querySelector('.cs-checkbox') as HTMLButtonElement;
        if (!checkbox) return;

        const isSelected = this.config.isSelected?.(item) ?? false;
        checkbox.setAttribute('aria-checked', String(isSelected));
        checkbox.classList.toggle('selected', isSelected);
    }

    private attachRowEvents(row: HTMLElement, renderedRow: RenderedRow<T>): void {
        // All handlers read from renderedRow object to get current values
        row.addEventListener('click', (e: Event) => {
            this.config.onRowClick?.(renderedRow.item, renderedRow.index, e as MouseEvent);
        });

        row.addEventListener('dblclick', (e: Event) => {
            this.config.onRowDoubleClick?.(renderedRow.item, renderedRow.index, e as MouseEvent);
        });

        row.addEventListener('keydown', (e: Event) => {
            const keyEvent = e as KeyboardEvent;
            // First let external handler try
            if (this.config.onKeyDown?.(keyEvent, renderedRow.item)) {
                return;
            }
            // Then handle navigation
            this.handleKeyDown(keyEvent);
        });

        row.addEventListener('focus', () => {
            this.focusedIndex = renderedRow.index;
        });
    }

    private applyRowState(row: HTMLElement, item: T): void {
        const state = this.config.getRowState?.(item);
        const currentFresh = row.classList.contains('fresh');
        const currentDeleted = row.classList.contains('deleted');
        const currentVisited = row.classList.contains('visited');
        const currentUnread = row.classList.contains('unread');

        const newFresh = state?.fresh ?? false;
        const newDeleted = state?.deleted ?? false;
        const newVisited = state?.visited ?? false;
        const newUnread = state?.unread ?? false;

        // Only toggle if changed
        if (currentFresh !== newFresh) row.classList.toggle('fresh', newFresh);
        if (currentDeleted !== newDeleted) row.classList.toggle('deleted', newDeleted);
        if (currentVisited !== newVisited) row.classList.toggle('visited', newVisited);
        if (currentUnread !== newUnread) row.classList.toggle('unread', newUnread);

        // Custom classes
        const prevCustom = (row.dataset.rsClasses ?? '').split(' ').filter(Boolean);
        const newCustom = state?.classes;
        for (const cls of prevCustom) {
            if (!newCustom?.has(cls)) row.classList.remove(cls);
        }
        if (newCustom) {
            for (const cls of newCustom) row.classList.add(cls);
            row.dataset.rsClasses = Array.from(newCustom).join(' ');
        } else if (prevCustom.length > 0) {
            delete row.dataset.rsClasses;
        }

        // Active row state (master-detail highlight, separate from checkbox selection)
        if (this.config.isActive) {
            const isActive = this.config.isActive(item);
            const currentActive = row.classList.contains('selected');
            if (currentActive !== isActive) {
                row.classList.toggle('selected', isActive);
            }
        }
    }

    private getHeatMapBounds(columns: ColumnDef<T>[]): Map<string, HeatMapBounds> {
        const bounds = new Map<string, HeatMapBounds>();
        for (const col of columns) {
            if (col.heatMap?.enabled) {
                const b = this.columnManager.getHeatMapBounds(col.key);
                if (b) bounds.set(col.key, b);
            }
        }
        return bounds;
    }

    updateHeatMap(bounds: Map<string, HeatMapBounds>): void {
        // Update shading for all visible rows
        for (const row of this.renderedRows.values()) {
            for (const [columnKey, renderedCell] of row.cells) {
                const column = this.columnManager.getColumnByKey(columnKey);
                if (!column?.heatMap?.enabled) continue;

                const columnBounds = bounds.get(columnKey);
                if (!columnBounds) continue;

                const value = safeGetValue(column, row.item);
                const shadeRank = computeShadeRank(value, columnBounds, column, row.item);

                const ctx: CellRenderContext<T> = {
                    column,
                    item: row.item,
                    value,
                    rowIndex: row.index,
                    heatMapBounds: columnBounds,
                    shadeRank
                };

                // Update just shading via factory
                const updatedCell = this.rendererFactory.updateTableCell(
                    renderedCell,
                    ctx,
                    CellUpdateType.Shades
                );

                row.cells.set(columnKey, updatedCell);
            }
        }
    }

    /**
     * Render the header row.
     * Should be called once and placed in a sticky container.
     */
    renderHeader(container: HTMLElement): HTMLElement {
        const header = document.createElement('div');
        header.className = 'cs-table-header';
        header.setAttribute('role', 'row');

        // Checkbox header (if selectable)
        if (this.selectable) {
            const cell = document.createElement('div');
            cell.className = 'cs-table-header-cell cs-selector';
            cell.setAttribute('role', 'columnheader');
            header.appendChild(cell);
        }

        // Column headers
        const columns = this.columnManager.getVisibleColumns();
        for (const column of columns) {
            if (column.table?.ignore) continue;

            const cell = document.createElement('div');
            cell.className = 'cs-table-header-cell';
            cell.setAttribute('role', 'columnheader');
            cell.setAttribute('data-column', column.key);
            cell.textContent = column.header;

            // Sort indicator
            if (column.sortable) {
                cell.classList.add('sortable');
                cell.setAttribute('aria-sort', 'none');

                if (this.config.sortColumn === column.key) {
                    cell.classList.add('sorted');
                    cell.setAttribute('aria-sort', this.config.sortDirection === 'asc' ? 'ascending' : 'descending');

                    const indicator = document.createElement('span');
                    indicator.className = 'sort-indicator';
                    indicator.setAttribute('aria-hidden', 'true');
                    indicator.textContent = this.config.sortDirection === 'asc' ? '▲' : '▼';
                    cell.appendChild(indicator);
                }

                cell.addEventListener('click', (e: Event) => {
                    this.config.onHeaderClick?.(column.key, e as MouseEvent);
                });
            }

            header.appendChild(cell);
        }

        this.headerElement = header;
        container.appendChild(header);
        return header;
    }

    /**
     * Sync header column widths with body (fallback for auto-sized columns).
     * Uses requestAnimationFrame to ensure layout is complete.
     */
    syncHeaderWidths(): void {
        if (!this.headerElement || !this.container) return;
        if (!this.hasAutoSizedColumns()) return;

        requestAnimationFrame(() => {
            if (!this.headerElement || !this.container) return;

            const firstRow = this.container.querySelector('.cs-table-row');
            if (!firstRow) return;

            const bodyCells = firstRow.querySelectorAll('.cs-table-cell');
            const widths: string[] = [];

            bodyCells.forEach((cell) => {
                widths.push(cell.getBoundingClientRect().width + 'px');
            });

            this.rootElement.style.setProperty('--cs-table-columns', widths.join(' '));
        });
    }

    /**
     * Check if any columns have auto-sized widths.
     */
    hasAutoSizedColumns(): boolean {
        const columns = this.columnManager.getVisibleColumns();
        return columns.some(c => c.width === undefined || c.table?.width === undefined);
    }

    updateItem(index: number, item: T): void {
        const row = this.renderedRows.get(index);
        if (row) {
            this.updateRowElement(row, item, index);
        }
    }

    updateItems(updates: Array<{ index: number; item: T }>): void {
        // Cache columns for batch update
        this.cachedColumns = this.columnManager.getVisibleColumns();
        this.cachedHeatMapBounds = this.getHeatMapBounds(this.cachedColumns);

        for (const { index, item } of updates) {
            const row = this.renderedRows.get(index);
            if (row) {
                this.updateRowElement(row, item, index);
            }
        }

        // Clear cache
        this.cachedColumns = null;
        this.cachedHeatMapBounds = null;
    }

    markItemDeleted(index: number): void {
        const row = this.renderedRows.get(index);
        if (row) {
            row.element.classList.add('deleted');
        }
    }

    markItemFresh(index: number): void {
        const row = this.renderedRows.get(index);
        if (row) {
            row.element.classList.add('fresh');
            // Track timeout for proper cleanup
            this.timeoutManager.schedule(`fresh-${index}`, () => {
                // Check if row still exists
                const currentRow = this.renderedRows.get(index);
                if (currentRow && currentRow.element === row.element) {
                    row.element.classList.remove('fresh');
                }
            }, this.freshDuration);
        }
    }

    setSelectable(selectable: boolean): void {
        if (this.selectable === selectable) return;

        this.selectable = selectable;

        // Update column template (adds/removes checkbox column)
        this.updateColumnTemplate();

        // Must re-render to add/remove checkbox column
        // Clear all rows and header
        for (const row of this.renderedRows.values()) {
            this.cleanupRow(row);
            row.element.remove();
        }
        this.renderedRows.clear();

        // Re-render header if it exists
        if (this.headerElement) {
            const parent = this.headerElement.parentElement;
            this.headerElement.remove();
            this.headerElement = null;
            if (parent) {
                this.renderHeader(parent);
            }
        }
    }

    /**
     * Update sort display.
     */
    setSortColumn(column: string | undefined, direction: 'asc' | 'desc'): void {
        this.config.sortColumn = column;
        this.config.sortDirection = direction;

        // Update header sort indicators
        if (this.headerElement) {
            const cells = this.headerElement.querySelectorAll('.cs-table-header-cell');
            cells.forEach(cell => {
                const key = cell.getAttribute('data-column');
                if (!key) return; // Skip checkbox column

                const isSorted = key === column;

                cell.classList.toggle('sorted', isSorted);
                cell.setAttribute('aria-sort', isSorted
                    ? (direction === 'asc' ? 'ascending' : 'descending')
                    : 'none'
                );

                // Update or add/remove indicator
                let indicator = cell.querySelector('.sort-indicator');
                if (isSorted) {
                    if (!indicator) {
                        indicator = document.createElement('span');
                        indicator.className = 'sort-indicator';
                        indicator.setAttribute('aria-hidden', 'true');
                        cell.appendChild(indicator);
                    }
                    indicator.textContent = direction === 'asc' ? '▲' : '▼';
                } else if (indicator) {
                    indicator.remove();
                }
            });
        }
    }

    setTotalCount(count: number): void {
        this.totalCount = count;
        // Update ARIA on container if needed
        if (this.container) {
            this.container.setAttribute('aria-rowcount', String(count));
        }
    }

    onColumnsChanged(): void {
        // Update column template
        this.updateColumnTemplate();

        // Clear all rendered rows and re-render
        for (const row of this.renderedRows.values()) {
            this.cleanupRow(row);
            row.element.remove();
        }
        this.renderedRows.clear();
        this.timeoutManager.cancelAll();

        // Re-render header if it exists
        if (this.headerElement) {
            const parent = this.headerElement.parentElement;
            this.headerElement.remove();
            this.headerElement = null;
            if (parent) {
                this.renderHeader(parent);
            }
        }
    }

    invalidate(): void {
        // Clear all rendered rows without touching column template
        for (const row of this.renderedRows.values()) {
            this.cleanupRow(row);
            row.element.remove();
        }
        this.renderedRows.clear();
        this.timeoutManager.cancelAll();
    }

    focusItem(index: number): void {
        const row = this.renderedRows.get(index);
        if (row) {
            row.element.focus();
            this.focusedIndex = index;
        }
    }

    getFocusedIndex(): number {
        return this.focusedIndex;
    }

    handleKeyDown(event: KeyboardEvent): boolean {
        const currentIndex = this.focusedIndex;
        if (currentIndex < 0) return false;

        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                const nextIndex = currentIndex + 1;
                if (this.renderedRows.has(nextIndex)) {
                    this.focusItem(nextIndex);
                    return true;
                }
                // Return false to allow parent to scroll
                return false;
            }
            case 'ArrowUp': {
                event.preventDefault();
                const prevIndex = currentIndex - 1;
                if (prevIndex >= 0 && this.renderedRows.has(prevIndex)) {
                    this.focusItem(prevIndex);
                    return true;
                }
                // Return false to allow parent to scroll
                return false;
            }
            case 'Home': {
                if (event.ctrlKey) {
                    event.preventDefault();
                    // Focus first rendered row
                    const minIndex = Math.min(...this.renderedRows.keys());
                    if (minIndex !== Infinity) {
                        this.focusItem(minIndex);
                        return true;
                    }
                }
                return false;
            }
            case 'End': {
                if (event.ctrlKey) {
                    event.preventDefault();
                    // Focus last rendered row
                    const maxIndex = Math.max(...this.renderedRows.keys());
                    if (maxIndex !== -Infinity) {
                        this.focusItem(maxIndex);
                        return true;
                    }
                }
                return false;
            }
            case 'Enter': {
                event.preventDefault();
                const row = this.renderedRows.get(currentIndex);
                if (row) {
                    // Simulate click
                    row.element.click();
                }
                return true;
            }
            case ' ': {
                event.preventDefault();
                const row = this.renderedRows.get(currentIndex);
                if (row && this.selectable) {
                    this.config.onSelectionToggle?.(row.item, new MouseEvent('click'));
                }
                return true;
            }
        }

        return false;
    }

    getItemElement(index: number): HTMLElement | null {
        return this.renderedRows.get(index)?.element ?? null;
    }

    getRenderedElements(): HTMLElement[] {
        return Array.from(this.renderedRows.values()).map(r => r.element);
    }

    /**
     * Get the row height.
     */
    getRowHeight(): number {
        return this.rowHeight;
    }

    dispose(): void {
        this.timeoutManager.dispose();
        this.eventTracker.dispose();

        for (const row of this.renderedRows.values()) {
            this.cleanupRow(row);
        }
        this.renderedRows.clear();
    }
}
